import { useState, useEffect, useCallback } from 'react';
import { X, Loader2 } from 'lucide-react';
import api from '@/lib/api';
import { useQueue } from '@/components/QueueContext';

const APP_COLORS = { giraffe: '#10B981', arx: '#C0A96A', uncode: '#E85D24', cult: '#F5A623' };
const CONFIDENCE_COLORS = { high: '#10B981', medium: '#E8C547', low: '#E24B4A' };

function relativeTime(isoStr) {
  if (!isoStr) return '';
  const diff = Date.now() - new Date(isoStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

function extractPreviewFields(item) {
  const d = item.extracted_data || {};
  const parts = [];
  if (d.date) {
    const dateStr = d.end_date && d.end_date !== d.date ? `${d.date} – ${d.end_date}` : d.date;
    parts.push(dateStr);
  }
  if (d.cost_zar) parts.push(`R${Number(d.cost_zar).toLocaleString()}`);
  if (d.booking_ref) parts.push(`Ref: ${d.booking_ref}`);
  if (d.category) parts.push(d.category);
  if (d.urgency) parts.push(d.urgency);
  if (d.domain) parts.push(d.domain);
  return parts.slice(0, 3);
}

function QueueCard({ item, onRefresh }) {
  const [mode, setMode] = useState('default'); // default | reject-confirm | edit | loading
  const [error, setError] = useState('');
  const [editData, setEditData] = useState(() => JSON.stringify(item.extracted_data || {}, null, 2));
  const [editSuggested, setEditSuggested] = useState(() => JSON.stringify(item.suggested_entry || {}, null, 2));
  const [visible, setVisible] = useState(true);

  const animateOut = (cb) => {
    setVisible(false);
    setTimeout(cb, 300);
  };

  const doAction = async (action, body) => {
    setMode('loading');
    setError('');
    try {
      if (body) {
        await api.put(`/queue/${item.id}/${action}`, body);
      } else {
        await api.put(`/queue/${item.id}/${action}`);
      }
      animateOut(onRefresh);
    } catch (e) {
      setError(e.response?.data?.detail || 'Action failed');
      setMode('default');
    }
  };

  const handleApprove = () => doAction('approve');
  const handleLater = () => doAction('later');
  const handleReject = () => doAction('reject');

  const handleEditApprove = () => {
    let extracted, suggested;
    try { extracted = JSON.parse(editData); } catch { setError('extracted_data is not valid JSON'); return; }
    try { suggested = JSON.parse(editSuggested); } catch { setError('suggested_entry is not valid JSON'); return; }
    doAction('edit', { extracted_data: extracted, suggested_entry: suggested });
  };

  const previewFields = extractPreviewFields(item);
  const titleText = item.subject || (item.raw_content || '').split('\n')[0].slice(0, 80) || 'No subject';

  if (!visible) {
    return <div style={{ height: 0, overflow: 'hidden', transition: 'height 0.3s ease', opacity: 0 }} />;
  }

  return (
    <div style={{
      background: 'rgba(255,255,255,0.03)',
      border: '0.5px solid rgba(255,255,255,0.06)',
      borderRadius: 12,
      padding: '14px 16px',
      margin: '8px 16px',
      transition: 'opacity 0.3s ease',
      opacity: visible ? 1 : 0,
      position: 'relative',
    }}>
      {/* Confidence dot */}
      <div style={{
        position: 'absolute', top: 14, right: 16,
        width: 6, height: 6, borderRadius: '50%',
        background: CONFIDENCE_COLORS[item.confidence] || '#b0b4c0',
      }} />

      {/* Pills row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap', paddingRight: 16 }}>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
          background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)',
          borderRadius: 4, padding: '2px 6px', color: '#b0b4c0',
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{item.source || 'email'}</span>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", fontSize: 9,
          borderRadius: 4, padding: '2px 6px',
          background: `${APP_COLORS[item.routed_to]}22`,
          color: APP_COLORS[item.routed_to] || '#b0b4c0',
          border: `0.5px solid ${APP_COLORS[item.routed_to]}44`,
          textTransform: 'uppercase', letterSpacing: '0.08em',
        }}>{item.routed_to}</span>
      </div>

      {/* Subject / title */}
      <p style={{ fontSize: 13, color: '#f0f0f5', margin: '0 0 4px', lineHeight: 1.4 }}>{titleText}</p>

      {/* Preview fields */}
      {previewFields.length > 0 && (
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#b0b4c0', margin: '0 0 8px' }}>
          {previewFields.join(' · ')}
        </p>
      )}

      {/* Received time */}
      <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: '#454860', marginBottom: 12 }}>
        {relativeTime(item.received_at)}
      </p>

      {/* Edit form */}
      {mode === 'edit' && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ marginBottom: 8 }}>
            <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#b0b4c0', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              extracted_data
            </label>
            <textarea
              value={editData}
              onChange={e => setEditData(e.target.value)}
              rows={6}
              style={{
                width: '100%', background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)',
                borderRadius: 6, padding: '8px 10px', color: '#f0f0f5',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                outline: 'none', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
          <div>
            <label style={{ display: 'block', fontFamily: "'JetBrains Mono', monospace", fontSize: 9, color: '#b0b4c0', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 4 }}>
              suggested_entry
            </label>
            <textarea
              value={editSuggested}
              onChange={e => setEditSuggested(e.target.value)}
              rows={6}
              style={{
                width: '100%', background: '#0a0a0f', border: '0.5px solid rgba(255,255,255,0.06)',
                borderRadius: 6, padding: '8px 10px', color: '#f0f0f5',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                outline: 'none', resize: 'vertical', boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      )}

      {/* Error text */}
      {error && (
        <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#E24B4A', marginBottom: 8 }}>{error}</p>
      )}

      {/* Action buttons */}
      {mode === 'default' && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {[
            { label: 'Later', onClick: handleLater, style: {} },
            { label: 'Edit', onClick: () => setMode('edit'), style: {} },
            { label: 'Approve', onClick: handleApprove, style: { borderColor: '#10B981', color: '#10B981' } },
            { label: 'Reject', onClick: () => setMode('reject-confirm'), style: {} },
          ].map(btn => (
            <button
              key={btn.label}
              onClick={btn.onClick}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8,
                padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                color: '#f0f0f5', cursor: 'pointer', transition: 'opacity 0.15s',
                ...btn.style,
              }}
            >{btn.label}</button>
          ))}
        </div>
      )}

      {mode === 'reject-confirm' && (
        <div>
          <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#b0b4c0', marginBottom: 8 }}>
            Permanently discard this item?
          </p>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={() => setMode('default')}
              style={{
                background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8,
                padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                color: '#f0f0f5', cursor: 'pointer',
              }}
            >Cancel</button>
            <button
              onClick={handleReject}
              style={{
                background: '#E24B4A', border: 'none', borderRadius: 8,
                padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
                color: '#f0f0f5', cursor: 'pointer',
              }}
            >Yes, reject</button>
          </div>
        </div>
      )}

      {mode === 'edit' && (
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={() => setMode('default')}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '0.5px solid rgba(255,255,255,0.06)', borderRadius: 8,
              padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
              color: '#f0f0f5', cursor: 'pointer',
            }}
          >Cancel</button>
          <button
            onClick={handleEditApprove}
            style={{
              background: 'rgba(255,255,255,0.05)', border: '0.5px solid #10B981', borderRadius: 8,
              padding: '8px 16px', fontSize: 12, fontFamily: "'JetBrains Mono', monospace",
              color: '#10B981', cursor: 'pointer',
            }}
          >Approve with edits</button>
        </div>
      )}

      {mode === 'loading' && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Loader2 size={14} style={{ color: '#b0b4c0', animation: 'spin 1s linear infinite' }} />
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#b0b4c0' }}>Processing…</span>
        </div>
      )}
    </div>
  );
}

const TABS = ['all', 'giraffe', 'arx', 'uncode', 'cult'];

export default function QueuePanel() {
  const { count, panelOpen, panelFilter, closeQueue, setPanelFilter, refreshCount } = useQueue();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(panelFilter || 'all');

  // Sync tab with panelFilter when panel opens
  useEffect(() => {
    if (panelOpen) {
      setActiveTab(panelFilter || 'all');
    }
  }, [panelOpen, panelFilter]);

  const loadItems = useCallback(async (tab) => {
    setLoading(true);
    try {
      const params = tab && tab !== 'all' ? { params: { app: tab } } : {};
      const { data } = await api.get('/queue', params);
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (panelOpen) {
      loadItems(activeTab);
    }
  }, [panelOpen, activeTab, loadItems]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setPanelFilter(tab === 'all' ? null : tab);
  };

  const handleRefresh = () => {
    refreshCount();
    loadItems(activeTab);
  };

  if (!panelOpen) return null;

  const tabCount = (tab) => {
    if (tab === 'all') return count.total;
    return count.by_app[tab] || 0;
  };

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={closeQueue}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(0,0,0,0.6)',
          zIndex: 199,
        }}
      />

      {/* Panel */}
      <div style={{
        position: 'fixed',
        bottom: 0, left: 0, right: 0,
        maxHeight: '75vh',
        background: '#0a0a0f',
        borderTop: '0.5px solid rgba(255,255,255,0.06)',
        borderRadius: '24px 24px 0 0',
        zIndex: 200,
        overflowY: 'auto',
        transform: panelOpen ? 'translateY(0)' : 'translateY(100%)',
        transition: 'transform 0.3s ease',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '20px 20px 12px',
          position: 'sticky', top: 0, background: '#0a0a0f', zIndex: 1,
          borderBottom: '0.5px solid rgba(255,255,255,0.04)',
        }}>
          <div>
            <h2 style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: 22, fontWeight: 300, color: '#f0f0f5', margin: 0 }}>
              Zephyr Queue
            </h2>
            <p style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11, color: '#b0b4c0', margin: '4px 0 0' }}>
              {count.total} item{count.total !== 1 ? 's' : ''} pending
            </p>
          </div>
          <button
            onClick={closeQueue}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#b0b4c0', padding: 4 }}
          >
            <X size={18} />
          </button>
        </div>

        {/* Tab row */}
        <div style={{
          display: 'flex', overflowX: 'auto', padding: '0 16px',
          borderBottom: '0.5px solid rgba(255,255,255,0.04)', gap: 0,
        }}>
          {TABS.map(tab => (
            <button
              key={tab}
              onClick={() => handleTabChange(tab)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '10px 12px', whiteSpace: 'nowrap',
                fontFamily: "'JetBrains Mono', monospace", fontSize: 11,
                color: activeTab === tab ? '#E8C547' : '#b0b4c0',
                borderBottom: activeTab === tab ? '2px solid #E8C547' : '2px solid transparent',
                marginBottom: -1,
                transition: 'color 0.2s',
              }}
            >
              {tab === 'all' ? 'All' : tab.charAt(0).toUpperCase() + tab.slice(1)}
              {' '}({tabCount(tab)})
            </button>
          ))}
        </div>

        {/* Items */}
        <div style={{ paddingBottom: 24 }}>
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 32 }}>
              <Loader2 size={20} style={{ color: '#b0b4c0', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : items.length === 0 ? (
            <p style={{ textAlign: 'center', fontFamily: "'JetBrains Mono', monospace", fontSize: 12, color: '#454860', padding: 32 }}>
              Queue clear
            </p>
          ) : (
            items.map(item => (
              <QueueCard key={item.id} item={item} onRefresh={handleRefresh} />
            ))
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      `}</style>
    </>
  );
}
